import { plugins } from "@citation-js/core";
import { isBundledOutputLocale } from "@/lib/output-locales";

/**
 * Registers vendored CSL locale definitions with citeproc, on demand.
 *
 * `@citation-js/plugin-csl` bundles five locales. The other twelve the site offers live
 * in `src/data/csl-locales/` (see `scripts/generate-csl-locales.mjs`) and are handed to
 * the engine's locale register the first time one is asked for.
 *
 * Three properties this file is responsible for keeping true:
 *
 *  1. **No network.** Locales arrive by `import()`, not `fetch()`. The golden test
 *     suite stubs fetch and throws on any call, so citation output stays deterministic
 *     and offline. Vendoring is what buys that.
 *  2. **English is untouched.** `en-US` and the other four bundled locales return
 *     immediately, so the default path does exactly what it did before this file
 *     existed — the byte-identical-output rule the golden snapshots pin.
 *  3. **Loaded once.** The promise is cached per locale, so twenty entries in one
 *     request share a single import rather than racing twenty of them.
 *
 * The loader map is written out rather than built from a template literal because
 * bundlers can only trace `import()` calls whose specifier is a literal — a computed
 * path would compile, then fail at runtime in production with a missing chunk. Keys are
 * canonical CSL locale codes; file names are their lower-case form, which the repo's
 * lint rules require.
 */

const LOADERS: Record<string, () => Promise<{ default: string }>> = {
  "pt-BR": () => import("@/data/csl-locales/pt-br"),
  "it-IT": () => import("@/data/csl-locales/it-it"),
  "pl-PL": () => import("@/data/csl-locales/pl-pl"),
  "tr-TR": () => import("@/data/csl-locales/tr-tr"),
  "ru-RU": () => import("@/data/csl-locales/ru-ru"),
  "id-ID": () => import("@/data/csl-locales/id-id"),
  "vi-VN": () => import("@/data/csl-locales/vi-vn"),
  "hi-IN": () => import("@/data/csl-locales/hi-in"),
  "ja-JP": () => import("@/data/csl-locales/ja-jp"),
  "ko-KR": () => import("@/data/csl-locales/ko-kr"),
  "zh-CN": () => import("@/data/csl-locales/zh-cn"),
  ar: () => import("@/data/csl-locales/ar"),
};

/** Locales this module can register — every output locale that is not bundled. */
export const VENDORED_LOCALE_CODES = Object.keys(LOADERS);

const inFlight = new Map<string, Promise<void>>();

async function register(locale: string): Promise<void> {
  const load = LOADERS[locale];
  if (!load) {
    return;
  }
  const { default: xml } = await load();
  // `plugins.config.get("@csl").locales` is a Register keyed by locale code; its values
  // are the raw CSL locale XML, which is what citeproc parses.
  const config = plugins.config.get("@csl") as {
    locales: {
      has(code: string): boolean;
      add(code: string, xml: string): void;
    };
  };
  if (!config.locales.has(locale)) {
    config.locales.add(locale, xml);
  }
}

/**
 * Make `locale` available to citeproc. A no-op for bundled locales and for anything
 * unrecognised — an unknown code degrades to citeproc's own fallback rather than
 * throwing, so a bad locale can never cost someone their citation.
 */
export function ensureLocaleRegistered(locale: string): Promise<void> {
  if (isBundledOutputLocale(locale) || !LOADERS[locale]) {
    return Promise.resolve();
  }
  let pending = inFlight.get(locale);
  if (!pending) {
    // A failed load must not poison the cache: drop it so the next call retries, and
    // swallow the error so formatting continues in citeproc's fallback locale.
    pending = register(locale).catch(() => {
      inFlight.delete(locale);
    });
    inFlight.set(locale, pending);
  }
  return pending;
}
